import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AlertChannel } from '../../shared/models/AlertChannel';
import { Severity } from '../../shared/models/Severity';
import { AlertsService } from '../../shared/services/alerts.service';
import { SelectionService } from '../../shared/services/selection.service';
import { BrandStoreService } from '../geo-doe-config/data-stores/brand-store.service';
import { NotificationCenterService } from '../notification-center/notification-center.service';
import { CriticalAlertBannerService } from './critical-alert-banner.service';

@Component({
  selector: 'portal-critical-alert-banner',
  templateUrl: './critical-alert-banner.component.html',
  styleUrls: ['./critical-alert-banner.component.scss'],
  standalone: false
})
export class CriticalAlertBannerComponent implements OnInit, OnDestroy {
  public alertCount = 0;
  private readonly alertSeverity = [Severity.CRITICAL];
  private clientId!: number | undefined;
  private brandId!: number | undefined;
  private routeSubscription!: Subscription;
  private criticalAlertBannerSubscription!: Subscription;

  constructor(
    private readonly alertsService: AlertsService,
    private readonly brandStoreService: BrandStoreService,
    private readonly router: Router,
    private readonly selectionService: SelectionService,
    private readonly notificationCenterService: NotificationCenterService,
    private readonly criticalAlertBannerService: CriticalAlertBannerService
  ) {}

  ngOnInit(): void {
    this.initBrand();
    this.initSubs();
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    if (this.criticalAlertBannerSubscription) {
      this.criticalAlertBannerSubscription.unsubscribe();
    }
  }

  private initSubs(): void {
    this.routeSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.initBrand();
      }
    });
    this.selectionService.selectionChanged.subscribe(() => {
      this.initBrand();
    });
    this.criticalAlertBannerSubscription =
      this.criticalAlertBannerService.refreshCriticalAlertBanner$.subscribe(
        () => {
          this.getAlertCount();
        }
      );
  }

  private initBrand(): void {
    const { clientId, brandId } = this.brandStoreService.brand ?? {};
    this.clientId = clientId;
    this.brandId = brandId;
    this.getAlertCount();
  }

  public getAlertCount(): void {
    if (!this.clientId || !this.brandId) {
      return;
    }
    this.alertsService
      .getAlertsCountWithFilters(
        this.clientId,
        this.brandId,
        this.alertSeverity,
        [AlertChannel.UI]
      )
      .subscribe({
        next: (count) => {
          this.alertCount = count.actionableAlert;
          this.criticalAlertBannerService.criticalAlertsCount.next(
            this.alertCount
          );
        },
        error: (err) => {
          console.error('Failed to fetch alert count:', err);
          this.alertCount = 0;
        }
      });
  }

  public navigateToNotificationCenter(): void {
    this.notificationCenterService.setCriticalSeverityFilter();

    this.router
      .navigate([
        `/a/${this.selectionService.buildSelectionSlug()}/products/notification-center`
      ])
      .catch((e) => console.error(e));
  }
}
